<script>
import set from "lodash/set";
import get from "lodash/get";
import {
  AdobeFunctions,
  applicationProps
} from "../../../pixels/providers/utils/adobeFunctions";
import { typologyTagging } from "../../../utils/helpers";
import store from "../../../store";

/**
 * Configuración de circuito origen
 * @notice Se debe configurar en cada circuito
 */
const circuitoOrigen = {
  name: applicationProps.application.name,
  operationNumber: store.getters.getNumeroTramite() || "",
  typology: typologyTagging() || ""
};

/**
 * Functiones comunes de Adobe para Veridas
 */
const AdobeFunctionsVeridas = {
  currentDocumentPhase: "obverse",
  setPageInfo(pageName) {
    const segments = [
      get(window.digitalData, "page.pageInfo.sysEnv", "escritorio"),
      "publica",
      "personas",
      "identity verification"
    ];
    if (pageName) {
      segments.push(pageName);
      set(window.digitalData, "page.pageInfo.level2", pageName);
    }
    set(window.digitalData, "page.pageInfo.pageName", segments.join(":"));
    set(window.digitalData, "page.pageInfo.level1", "identity verification");
    set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
    set(window.digitalData, "page.pageInfo.version", "dvsexpress");
    set(window.digitalData, "page.pageInfo.pageSegment", "personas");
    set(window.digitalData, "page.pageInfo.projectName", "Open Market");
  },
  setCustomfields() {
    const segments = [
      "application:" + circuitoOrigen.name,
      "operationNumber:" + circuitoOrigen.operationNumber,
      "typology:" + circuitoOrigen.typology
    ];
    set(window.digitalData, "application.customFields", segments.join(","));
  },
  setLinkName(linkName) {
    set(window.digitalData, "page.pageActivity.link.name", linkName);
  }
};

/**
 * Mapeo de eventos de Veridas a variables de Adobe
 */
const VeridasPageVars = {
  DocumentMounted: {
    pageName: "document mounted"
  },
  DocumentCameraVideoPlayStarted: {
    pageName: "document camera video play started"
  },
  DocumentManualCapture: {
    pageName: "document manual capture"
  },
  DocumentObverseDetection: {
    pageName: "document obverse detection",
    preHook: () => {
      // Ya capturamos el frente, ahora el reverso
      AdobeFunctionsVeridas.currentDocumentPhase = "reverse";
    }
  },
  DocumentSuccessTickFinish: {
    pageName: "",
    preHook: () => {
      AdobeFunctionsVeridas.setPageInfo(
        "document success tick finish " +
          AdobeFunctionsVeridas.currentDocumentPhase
      );
    }
  },
  DocumentRepeatClicked: {
    pageName: "",
    eventName: "Internal Link",
    method: "digitalLink",
    preHook: () => {
      AdobeFunctionsVeridas.setPageInfo(
        "document success tick finish " +
          AdobeFunctionsVeridas.currentDocumentPhase
      );
      AdobeFunctionsVeridas.setLinkName("document repeat clicked");
    }
  },
  DocumentReverseDetection: {
    pageName: "document reverse detection",
    skipEvent: true, // Este evento solo se usa para el preHook
    preHook: () => {
      // Ya capturamos el reverso, reiniciar
      AdobeFunctionsVeridas.currentDocumentPhase = "obverse";
    }
  },
  DocumentProcessFinished: {
    pageName: "document process finished"
  },
  SelfieMounted: {
    pageName: "selfie mounted"
  },
  SelfieCameraStarted: {
    pageName: "selfie camera started"
  },
  SelfieCameraRecorderSetup: {
    pageName: "selfie camera recorder setup"
  },
  SelfieSuccessTickFinish: {
    pageName: "selfie success tick finish"
  },
  SelfieRepeatClicked: {
    pageName: "selfie success tick finish",
    eventName: "Internal Link",
    method: "digitalLink",
    preHook: () => {
      AdobeFunctionsVeridas.setLinkName("selfie repeat clicked");
    }
  },
  SelfieProcessFinished: {
    pageName: "selfie process finished"
  },
  VideoCameraStarted: {
    pageName: "video camera started"
  },
  VideoLoaded: {
    pageName: "video loaded"
  },
  VideoCameraRecorderSetup: {
    pageName: "video camera recorder setup"
  },
  VideoSuccessTickFinish: {
    preHook: () => {
      AdobeFunctionsVeridas.setPageInfo(
        "video success tick finish " +
          AdobeFunctionsVeridas.currentDocumentPhase
      );
      AdobeFunctionsVeridas.currentDocumentPhase = "reverse";
    }
  },
  VideoUnmounted: {
    pageName: "video unmounted"
  },
  ProcessCompleted: {
    pageName: "process completed"
  },
  DocumentDetectionTimeout: {
    errorPage: "document detection timeout"
  },
  DocumentCameraFailure: {
    errorPage: "document camera failure"
  },
  TokenNotAllowed: {
    errorPage: "token not allowed"
  },
  UnknownDocumentTypeError: {
    errorPage: "unknown document type error"
  },
  BlurredImageError: {
    errorPage: "blurred image error"
  },
  DocumentMountFailure: {
    errorPage: "document mount failure"
  },
  SelfieMountFailure: {
    errorPage: "selfie mount failure"
  },
  SelfieDependenciesError: {
    errorPage: "selfie dependencies error"
  },
  DocumentDependenciesError: {
    errorPage: "document dependencies error"
  },
  SelfieCameraRecorderSetupError: {
    errorPage: "selfie camera recorder setup error"
  },
  ConnectionError: {
    errorPage: "connection error"
  }
};

/**
 * Procesar el evento recibido desde Veridas
 */
const procesarEvento = event => {
  // Page Info
  if (event.pageName !== undefined) {
    AdobeFunctionsVeridas.setPageInfo(event.pageName);
  }

  if (event.errorPage) {
    // Si es una página de error, setear el pageName y errorPage
    AdobeFunctionsVeridas.setPageInfo("error page");
    set(window.digitalData, "page.pageInfo.errorPage", event.errorPage);
  }

  // Métodos adicionales
  if (event.preHook && typeof event.preHook === "function") {
    event.preHook();
  }

  // Hay eventos que solo usamos para el preHook
  // y que no requieren llamar a Adobe
  if (event.skipEvent) {
    return;
  }

  AdobeFunctions.callAdobeDTM(
    event.method || "digitalView",
    event.eventName || "Page View"
  );
};

export default {
  name: "VeridasAnalytics",
  data() {
    return {
      digitalDataBackup: null,
      backupTimer: null
    };
  },
  render() {
    return null;
  },
  mounted() {
    window.addEventListener("message", this.handleMessage);
  },
  destroyed() {
    window.removeEventListener("message", this.handleMessage);
  },
  methods: {
    handleMessage(event) {
      // Solo escuchar eventos de xpressid
      const origenes = ["xpressid", "bbvafrances.com", "bbva.com", "localhost"];
      if (event && origenes.some(o => event.origin.includes(o)) === false) {
        return;
      }

      this.$log.info("VeridasAnalytics: Evento recibido", {
        ...event.data
      });

      // Algunos eventos tienen un objeto infoEvent que contiene el codigo
      const code = event.data.code.includes("ProcessEvents")
        ? event.data?.infoEvent?.code
        : event.data?.code;

      // Si no conseguimos el codigo, no hacemos nada
      if (!code) {
        return;
      }

      this.$log.info("VeridasAnalytics: Código reconocido", code, {
        ...event.data
      });

      if (VeridasPageVars[code]) {
        // Hacer backup del datalayer principal
        // para evitar que se sobrescriba con datos de veridas
        this.backupDatalayer();

        // Limpiar las variables de Adobe
        AdobeFunctions.clearVars();
        AdobeFunctions.setConstants();
        AdobeFunctions.setUserVars(this.$store);
        AdobeFunctionsVeridas.setCustomfields();

        // Llamar al evento
        procesarEvento(VeridasPageVars[code]);

        // Restaurar el datalayer
        this.restoreDatalayer();
      }
    },
    backupDatalayer() {
      // Si ya hay un timer de restauración, cancelarlo
      if (this.backupTimer) {
        clearTimeout(this.backupTimer);
        this.backupTimer = null;
        return;
      }

      // Hacer backup del datalayer
      this.digitalDataBackup = JSON.parse(JSON.stringify(window.digitalData));
    },
    restoreDatalayer() {
      // Setear el timer para restaurar el datalayer
      // dando tiempo al servicio de enviar el evento
      this.backupTimer = setTimeout(() => {
        window.digitalData = this.digitalDataBackup;
        this.digitalDataBackup = null;
        this.backupTimer = null;
      }, 500);
    }
  }
};
</script>
